/* picture.js — Mesures sur des images décodées : bandes noires et image utile,
 * entrelacement et télécinéma, et l'écart entre deux images. Fonctions pures :
 * elles reçoivent des points, jamais un fichier. */
'use strict';

var BLACK = 16;          // luminance en deçà de laquelle un point est noir
var BLACK_SHARE = 0.995; // part de points noirs pour qu'une ligne le soit
var COMB = 12;           // amplitude minimale d'une dent de peigne
var MOTION = 8;          // écart minimal avec l'image précédente pour compter
// Mesuré : progressif ≤ 0,019, entrelacé ≥ 0,185.
var COMBED_FRAME = 0.06;
// Mesuré : même plan ≤ 0,03, plans distincts ≥ 0,71.
var SAME_SHOT = 0.35;

// Luminance Rec. 709 d'une image RGBA, coefficients sur 256.
function lumaFrom(rgba, width, height) {
  var out = new Uint8Array(width * height);
  for (var i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = (rgba[p] * 54 + rgba[p + 1] * 183 + rgba[p + 2] * 19) >> 8;
  }
  return out;
}

/* ---------- Bandes noires ---------- */

function rowIsBlack(luma, width, y, limit) {
  var off = y * width, dark = 0;
  for (var x = 0; x < width; x++) if (luma[off + x] <= limit) dark++;
  return dark >= width * BLACK_SHARE;
}

function colIsBlack(luma, width, x, y0, y1, limit) {
  var dark = 0, n = y1 - y0;
  for (var y = y0; y < y1; y++) if (luma[y * width + x] <= limit) dark++;
  return dark >= n * BLACK_SHARE;
}

// Épaisseur des bandes, en points de l'image. null pour une image toute noire.
function bordersOf(luma, width, height, opts) {
  var limit = (opts && opts.limit != null) ? opts.limit : BLACK;
  var top = 0;
  while (top < height && rowIsBlack(luma, width, top, limit)) top++;
  if (top === height) return null;
  var bottom = 0;
  while (bottom < height - top - 1 && rowIsBlack(luma, width, height - 1 - bottom, limit)) bottom++;
  var y0 = top, y1 = height - bottom;
  var left = 0;
  while (left < width && colIsBlack(luma, width, left, y0, y1, limit)) left++;
  if (left === width) return null;
  var right = 0;
  while (right < width - left - 1 && colIsBlack(luma, width, width - 1 - right, y0, y1, limit)) right++;
  return { top: top, bottom: bottom, left: left, right: right };
}

/* ---------- Entrelacement ---------- */

/* Part des points peignés : ceux qui s'écartent dans le même sens de la ligne
 * du dessus et de celle du dessous. `prev` écarte les points immobiles, sans
 * quoi un détail fin passerait pour de l'entrelacement. null si rien ne bouge. */
function combOf(luma, prev, width, height, box, opts) {
  var comb = (opts && opts.comb != null) ? opts.comb : COMB;
  var motion = (opts && opts.motion != null) ? opts.motion : MOTION;
  var b = box || { top: 0, bottom: 0, left: 0, right: 0 };
  var y0 = Math.max(1, b.top), y1 = Math.min(height - 1, height - b.bottom);
  var x0 = b.left, x1 = width - b.right;
  var combed = 0, total = 0;
  for (var y = y0; y < y1; y++) {
    var row = y * width, up = row - width, down = row + width;
    for (var x = x0; x < x1; x++) {
      var c = luma[row + x];
      if (prev && Math.abs(prev[row + x] - c) <= motion) continue;
      total++;
      var d1 = luma[up + x] - c, d2 = luma[down + x] - c;
      if ((d1 > comb && d2 > comb) || (d1 < -comb && d2 < -comb)) combed++;
    }
  }
  return total < (x1 - x0) * (y1 - y0) * 0.01 ? null : combed / total;
}

/* ---------- Écart entre deux images ---------- */

// Moyenne de chaque case d'une grille posée sur l'image utile.
function thumbOf(luma, width, height, box, cols, rows) {
  var b = box || { top: 0, bottom: 0, left: 0, right: 0 };
  var x0 = b.left, x1 = width - b.right, y0 = b.top, y1 = height - b.bottom;
  var W = x1 - x0, H = y1 - y0;
  var out = new Float64Array(cols * rows);
  for (var r = 0; r < rows; r++) {
    var ya = y0 + Math.floor(H * r / rows), yb = y0 + Math.floor(H * (r + 1) / rows);
    for (var c = 0; c < cols; c++) {
      var xa = x0 + Math.floor(W * c / cols), xb = x0 + Math.floor(W * (c + 1) / cols);
      var sum = 0, n = 0;
      for (var y = ya; y < yb; y++) {
        var off = y * width;
        for (var x = xa; x < xb; x++) { sum += luma[off + x]; n++; }
      }
      out[r * cols + c] = n ? sum / n : 0;
    }
  }
  return out;
}

/* Écart entre deux vignettes, niveau et contraste égalisés : reste la
 * disposition des masses, qui survit à un changement de définition. */
function shotDistance(a, b) {
  function normalize(v) {
    var m = 0, i;
    for (i = 0; i < v.length; i++) m += v[i];
    m /= v.length;
    var s = 0;
    for (i = 0; i < v.length; i++) s += (v[i] - m) * (v[i] - m);
    s = Math.sqrt(s / v.length) || 1;
    var out = new Float64Array(v.length);
    for (i = 0; i < v.length; i++) out[i] = (v[i] - m) / s;
    return out;
  }
  var na = normalize(a), nb = normalize(b), d = 0;
  for (var i = 0; i < na.length; i++) d += Math.abs(na[i] - nb[i]);
  return Math.round(d / na.length * 1000) / 1000;
}

function sameShot(a, b) { return shotDistance(a, b) <= SAME_SHOT; }

/* ---------- Synthèse sur plusieurs images ---------- */

// La plus petite valeur vue, les 5 % les plus basses écartées.
function lowest(values) {
  if (!values.length) return null;
  var v = values.slice().sort(function (a, b) { return a - b; });
  return v[Math.floor(v.length * 0.05)];
}

/* Bandes retenues : la plus petite vue de chaque côté. Une bande n'en est une
 * que si rien ne s'y trouve jamais — un plan sombre ou un sous-titre incrusté
 * ferait mentir une moyenne. */
function barsOf(frames) {
  var seen = frames.filter(function (f) { return f && f.borders; }).map(function (f) { return f.borders; });
  if (!seen.length) return null;
  var bars = {
    top: lowest(seen.map(function (b) { return b.top; })),
    bottom: lowest(seen.map(function (b) { return b.bottom; })),
    left: lowest(seen.map(function (b) { return b.left; })),
    right: lowest(seen.map(function (b) { return b.right; }))
  };
  bars.frames = seen.length;
  return bars;
}

// Cycle 3:2 : deux images peignées toutes les cinq, aux mêmes places.
function telecineScore(flags) {
  if (flags.length < 5) return 0;
  var best = 0;
  for (var phase = 0; phase < 5; phase++) {
    var hit = 0;
    for (var i = 0; i < flags.length; i++) {
      var pos = (i + phase) % 5;
      if (flags[i] === (pos === 3 || pos === 4)) hit++;
    }
    if (hit / flags.length > best) best = hit / flags.length;
  }
  return best;
}

// `runs` : suites d'images consécutives, chacune [{ borders, comb }].
function scanOf(runs, opts) {
  var limit = (opts && opts.combedFrame != null) ? opts.combedFrame : COMBED_FRAME;
  var measured = 0, combedFrames = 0, scores = [];
  runs.forEach(function (run) {
    var flags = [];
    run.forEach(function (f) {
      if (!f || f.comb == null) return;
      measured++;
      var c = f.comb >= limit;
      if (c) combedFrames++;
      flags.push(c);
    });
    if (flags.length >= 5) scores.push(telecineScore(flags));
  });
  if (!measured) return { mode: null, frames: 0, combed: 0, ratio: null, reason: 'immobile' };
  var ratio = combedFrames / measured;
  var telecine = scores.length ? scores.reduce(function (a, b) { return a + b; }, 0) / scores.length : 0;
  var mode;
  if (ratio >= 0.25 && ratio <= 0.55 && telecine >= 0.85) mode = 'telecine';
  else if (ratio >= 0.5) mode = 'interlaced';
  else if (ratio <= 0.05) mode = 'progressive';
  else mode = 'partial';
  return {
    mode: mode, frames: measured, combed: combedFrames,
    ratio: Math.round(ratio * 1000) / 1000,
    telecine: Math.round(telecine * 100) / 100
  };
}

/* Tout ensemble, ramené aux points du fichier. `geom` : { width, height,
 * analysisWidth } — seule la largeur est réduite à l'analyse. */
function summarise(runs, geom) {
  var flat = [];
  runs.forEach(function (r) { flat = flat.concat(r); });
  var bars = barsOf(flat);
  var scale = geom.analysisWidth ? geom.width / geom.analysisWidth : 1;
  var out = { scan: scanOf(runs, geom), bars: null, active: null, barsShare: null };
  if (bars) {
    var left = Math.round(bars.left * scale), right = Math.round(bars.right * scale);
    var w = geom.width - left - right, h = geom.height - bars.top - bars.bottom;
    out.bars = { top: bars.top, bottom: bars.bottom, left: left, right: right, frames: bars.frames };
    out.active = { width: w, height: h, aspect: h > 0 ? Math.round(w / h * 1000) / 1000 : null };
    out.barsShare = Math.round((1 - (w * h) / (geom.width * geom.height)) * 1000) / 1000;
  }
  return out;
}

function values(ordinal, res) {
  var subject = { kind: 'video', ordinal: ordinal };
  var out = [];
  function add(property, v) {
    if (v == null) return;
    out.push({ subject: subject, property: property, value: v, origin: 'measured', source: 'décodage' });
  }
  if (res.active) {
    add('activeWidth', res.active.width);
    add('activeHeight', res.active.height);
    add('activeAspectRatio', res.active.aspect);
    add('barsShare', res.barsShare);
  }
  if (res.scan && (res.scan.mode === 'progressive' || res.scan.mode === 'interlaced')) {
    add('scanType', res.scan.mode);
  } else if (res.scan && res.scan.mode === 'telecine') {
    // peigné pour les règles, mais signalé à part : la correction diffère
    add('scanType', 'interlaced');
    add('telecine', true);
  }
  return out;
}

// En deçà de quatre points, une bande ne se distingue pas d'un bord de compression.
function hasBars(res) {
  return !!(res && res.bars && (res.bars.top >= 4 || res.bars.bottom >= 4 || res.bars.left >= 4 || res.bars.right >= 4));
}

var api = {
  lumaFrom: lumaFrom, bordersOf: bordersOf, combOf: combOf,
  barsOf: barsOf, scanOf: scanOf, summarise: summarise, values: values,
  thumbOf: thumbOf, shotDistance: shotDistance, sameShot: sameShot, SAME_SHOT: SAME_SHOT,
  hasBars: hasBars, telecineScore: telecineScore,
  BLACK: BLACK, COMB: COMB, MOTION: MOTION, COMBED_FRAME: COMBED_FRAME
};
globalThis.MediaPicture = api;
export default api;
